<template>
    <div class="body bank-app" :dir="isRTL ? 'rtl' : 'ltr'">

        <Navbar :show-back-button="true" @back="navigateToDashboard"/>

        <div class="account-summary">
          <div class="account-type">
            {{ $t('dashboard.accountBalance') }}
          </div>

          <div class="account-balance">
            <span class="balance-label"></span>
            <span class="balance-amount">
              {{ formatRemainingCash(remainingCash) }} {{ formatBestanKari(remainingCash) }}
            </span>
          </div>
        </div>


        <div class="section-header">

            <!-- <button class="back-arrow" @click="navigateToDashboard">
                →
            </button> -->

            <h3 class="section-title mt-3" style="margin-right: 10px;">
                {{ $t('transactions.title') }}
            </h3>

        </div>


        <!-- Action Bar -->

        <div class="action-bar">

            <button class="print-btn" @click="printData">
            🖨️
            </button>


            <button 
                class="sort-btn"
                @click="toggleSortOrder"
                :title="sortOrder === 'desc' ? $t('transactions.sortAsc') : $t('transactions.sortDesc')"
            >

                {{ sortOrder === 'desc' ? '⬇️' : '⬆️' }}

                <span class="sort-text">
                    {{ sortOrder === 'desc' ? $t('transactions.desc') : $t('transactions.asc') }}
                </span>

            </button>


            <button class="filter-btn" @click="toggleFilterPanel" :title="$t('transactions.filters')">
                🎛️
                <span class="filter-text">{{ $t('transactions.filter') }}</span>

                <span v-if="activeFilterCount > 0" class="filter-badge">
                    {{ activeFilterCount }}
                </span>

            </button>


            <!-- Currency -->

            <div class="currency-selector">

                <label for="currency-select" class="currency-label">
                    {{ $t('transactions.currency') }}
                </label>

                <select
                    id="currency-select"
                    v-model="selectedCurrency"
                    @change="onCurrencyChange"
                    class="currency-select"
                    :disabled="loadingArz || arzList.length === 0"
                >

                    <option
                        v-for="arz in arzList"
                        :key="arz.idd"
                        :value="arz.idd"
                    >
                        {{ arz.nam }} ({{ arz.sign }})
                    </option>

                </select>

                <div v-if="loadingArz" class="currency-loading">
                    {{ $t('common.loading') }}
                </div>

            </div>


            <!-- Search -->

            <div class="search-container">

            <input
                type="text"
                v-model="searchQuery"
                :placeholder="$t('transactions.searchPlaceholder')"
                class="search-input"
            />

            <span class="search-icon">🔍</span>

            </div>

        </div>


        <!-- Filter Panel -->

        <div v-if="showFilterPanel" class="filter-panel">

            <div class="filter-header">

                <h4>{{ $t('transactions.filters') }}</h4>

                <button class="close-filter-btn" @click="closeFilterPanel">
                    ×
                </button>

            </div>


            <div class="filter-content">


                <!-- Transaction Type -->

                <div class="filter-group">

                    <label class="filter-label">
                        {{ $t('transactions.transactionType') }}
                    </label>

                    <div class="filter-options">

                        <label class="filter-option">

                            <input
                                type="checkbox"
                                v-model="filters.transactionType"
                                value="debit"
                            >

                            <span class="checkmark"></span>

                            {{ $t('transactions.debit') }}

                        </label>


                        <label class="filter-option">

                            <input
                                type="checkbox"
                                v-model="filters.transactionType"
                                value="credit"
                            >

                            <span class="checkmark"></span>

                            {{ $t('transactions.credit') }}

                        </label>

                    </div>

                </div>


                <!-- Date Range -->

                <div class="filter-group">

                    <label class="filter-label">
                        {{ $t('transactions.dateRange') }}
                    </label>

                    <div class="date-inputs">

                        <div class="date-input">

                            <label>{{ $t('transactions.fromDate') }}</label>

                            <DatePicker
                                v-model="filters.dateFrom"
                                format="jYYYY/jMM/jDD"
                                display-format="jYYYY/jMM/jDD"
                                :placeholder="$t('transactions.selectDate')"
                                :editable="true"
                                input-class="persian-date-picker"
                                :auto-submit="true"
                            />

                        </div>


                        <div class="date-input">

                            <label>{{ $t('transactions.toDate') }}</label>

                            <DatePicker
                                v-model="filters.dateTo"
                                format="jYYYY/jMM/jDD"
                                display-format="jYYYY/jMM/jDD"
                                :placeholder="$t('transactions.selectDate')"
                                :editable="true"
                                input-class="persian-date-picker"
                                :auto-submit="true"
                            />

                        </div>

                    </div>

                </div>


                <!-- Amount -->

                <div class="filter-group">

                    <label class="filter-label">
                        {{ $t('transactions.amountRange') }}
                    </label>

                    <div class="amount-inputs">

                        <div class="amount-input">
                            <label>{{ $t('transactions.from') }}</label>

                            <input
                                type="number"
                                v-model="filters.amountFrom"
                                placeholder="0"
                                class="amount-picker"
                            >
                        </div>

                        <div class="amount-input">
                            <label>{{ $t('transactions.to') }}</label>

                            <input
                                type="number"
                                v-model="filters.amountTo"
                                placeholder="∞"
                                class="amount-picker"
                            >
                        </div>

                    </div>

                </div>


                <!-- Filter Actions -->

                <div class="filter-actions">

                    <button class="apply-filters-btn" @click="applyFilters">
                        {{ $t('transactions.applyFilters') }}
                    </button>

                    <button class="clear-filters-btn" @click="clearFilters">
                        {{ $t('transactions.clearFilters') }}
                    </button>

                </div>

            </div>

        </div>


        <!-- Loading -->

        <div v-if="loading" class="loading-state">

          <div class="loading-spinner"></div>

          <span>
            {{ $t('common.loading') }}
          </span>

        </div>

        <!-- Accounting Data Section -->
        <div class="accounting-section" v-if="filteredData.length > 0">
            <div class="accounting-grid">
            <div 
                class="accounting-card" 
                v-for="(record, index) in paginatedData" 
                :key="index"
            >
                <div class="card-header">
                  <div class="record-detail">
                    <span class="detail-label">                
                        <span class="record-date">{{ formatPersianDate(record.Dates) }} - {{ record.Exhibit_Auto }}</span>
                    </span>
                  </div>               
                </div>
                <div class="card-body">
                  <div class="record-detail">
                    <div class="comment-section">
                        <span class="detail-value">{{ record.Comment2?.trim() || 'بدون شرح' }}</span>
                      </div>
                      <div class="amount-section">
                        <span v-if="selectedCurrency==1" class="record-amount" style="font-family: 'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif;" :class="{ 'debit': record.debitPrice, 'credit': record.creditPrice }">
                          {{ formatCurrency(record.debitPrice || record.creditPrice) }} 
                          <span style="font-size: medium;">{{record.ExhibitItem_arz2}}</span>
                        </span>
                        <span v-else class="record-amount" style="font-family: 'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif;" :class="{ 'debit': record.debitPrice, 'credit': record.creditPrice }">
                          {{ formatCurrency(record.DebitPrice_A || record.creditPrice_A) }} 
                          <span style="font-size: medium;">{{record.ExhibitItem_arz2}}</span>
                        </span>
                        <span class="transaction-badge" :class="record.debitPrice ? 'debit-badge' : 'credit-badge'">
                          {{ record.debitPrice ? 'بد' : 'بس' }}
                        </span>
                      </div>
                    </div>

                <!-- <div class="record-detail">
                    <span class="detail-label">نوع:</span>
                    <span class="detail-value type-badge" :class="record.debitPrice ? 'debit-badge' : 'credit-badge'">
                    {{ record.debitPrice ? 'بدهکار' : 'بستانکار' }}
                    </span>
                </div>
                <div class="record-detail" v-if="record.ExhibitItem_arz2">
                    <span class="detail-label">ارز:</span>
                    <span class="detail-value">{{ record.ExhibitItem_arz2 }}</span>
                </div> -->
                <div class="record-detail" v-if="record.St">
                    <span class="detail-label">توضیحات:</span>
                    <span class="detail-value">{{ record.St }}</span>
                  </div>
                  <span class="detail-value">{{ record.Ac }}</span>
                <button 
                    v-if="record.Ac == 1 || record.Ac == 2 || 
                    record.Ac == 24 || record.Ac == 25 || record.Ac == 600 ||
                    record.Ac == 601"
                    class="details-btn"
                    @click="openCommercialDetails(record)"
                    :disabled="commercialLoading"
                >
                    {{ commercialLoading && selectedInvoice === record.ID_Invoice ? 'در حال بارگذاری...' : 'جزئیات' }}
                </button>
                </div>
            </div>
            </div>
            
            <!-- Pagination Controls Bottom -->
            <div class="pagination-controls">
            <button 
                @click="prevPage" 
                :disabled="currentPage === 1"
                class="pagination-btn"
            >
                قبلی
            </button>
            <span class="page-info">
                صفحه {{ currentPage }} از {{ totalPages }}
            </span>
            <button 
                @click="nextPage" 
                :disabled="currentPage === totalPages"
                class="pagination-btn"
            >
                بعدی
            </button>
            </div>
        </div>

        <!-- Empty -->
        <div v-if="!loading && accountingData.length === 0" class="empty-state">
            <span>{{ $t('transactions.noTransactions') }}</span>
        </div>


        <!-- Commercial Details Modal -->
        <div v-if="showCommercialModal" class="modal-overlay" @click="closeCommercialModal">
          <div class="modal-content" @click.stop>
              <div class="modal-header">
              <h3>جزئیات بازرگانی</h3>
              <div class="modal-actions">
                  <!-- Print Button -->
                  <button 
                  v-if="commercialDetails.length > 0 && !commercialLoading && !commercialError"
                  class="print-modal-btn"
                  @click="printCommercialDetails"
                  title="چاپ جزئیات"
                  >
                  🖨️ چاپ
                  </button>
                  <button class="close-btn" @click="closeCommercialModal">×</button>
              </div>
              </div>
              
              <div class="modal-body">
              <!-- Loading State -->
              <div v-if="commercialLoading" class="modal-loading">
                  <div class="loading-spinner"></div>
                  <span>در حال دریافت اطلاعات جزئیات...</span>
              </div>

              <!-- Error State -->
              <div v-else-if="commercialError" class="modal-error">
                  <div class="error-icon">⚠️</div>
                  <h4>خطا در دریافت اطلاعات</h4>
                  <p>{{ commercialError }}</p>
                  <button class="retry-btn" @click="retryFetchCommercialDetails">
                  تلاش مجدد
                  </button>
              </div>

              <!-- Success State -->
              <div v-else-if="commercialDetails.length > 0" class="commercial-details">
                  <div class="details-grid">
                  <div 
                      v-for="(detail, index) in commercialDetails" 
                      :key="index"
                      class="detail-card"
                  >
                      <!-- Title Section -->
                      <div class="detail-section">
                      <h4 class="section-title">عنوان کالا</h4>
                      <div class="info-box">
                          <div class="info-row">
                          <span class="info-label">نام کالا:</span>
                          <span class="info-value">{{ detail.KalaName || 'نامشخص' }}</span>
                          </div>

                          <!-- Amari Section - Using bracket notation for numeric properties -->
                          <div class="info-row" v-if="getItemsWithLabels(detail, '13').length > 0">
                            <span class="info-label">آماری:</span>
                            <div class="multi-values">
                                <span class="multi-value-item" v-for="item in getItemsWithLabels(detail, '13')" :key="item.key">
                                  {{ item.label }}: {{ formatNumber(item.value) }}
                                </span>
                            </div>
                          </div>

                          <div class="info-row">
                          <span class="info-label">واحد:</span>
                          <span class="info-value">{{ detail.vahed || 'نامشخص' }}</span>
                          </div>
                      </div>
                      </div>

                      <!-- Values Section -->
                      <div class="detail-section">
                      <h4 class="section-title">مقادیر</h4>
                      <div class="info-box">
                          <!-- <div class="info-row">
                          <span class="info-label">مقدار 1:</span>
                          <span class="info-value">{{ formatNumber(detail.Value1) }}</span>
                          </div> -->
                          <!-- <div class="info-row" v-if="detail.Value2">
                          <span class="info-label">مقدار 2:</span>
                          <span class="info-value">{{ formatNumber(detail.Value2) }}</span>
                          </div>
                          <div class="info-row" v-if="detail.Value3">
                          <span class="info-label">مقدار 3:</span>
                          <span class="info-value">{{ formatNumber(detail.Value3) }}</span>
                          </div>
                          <div class="info-row" v-if="detail.Value4">
                          <span class="info-label">مقدار 4:</span>
                          <span class="info-value">{{ formatNumber(detail.Value4) }}</span>
                          </div> -->
                          <div class="info-row" v-if="detail.GM7">
                          <span class="info-label">وزن پر:</span>
                          <span class="info-value">{{ formatNumber(detail.GM7) }}</span>
                          </div>
                          <div class="info-row" v-if="detail.GM8">
                          <span class="info-label">وزن خالی:</span>
                          <span class="info-value">{{ formatNumber(detail.GM8) }}</span>
                          </div>
                          <div class="info-row" v-if="detail.GM6">
                          <span class="info-label">مقدار ناخالص:</span>
                          <span class="info-value">{{ formatNumber(detail.GM6) }}</span>
                          </div>
                          <!-- Ksorat Section - Using bracket notation for numeric properties -->
                          <div class="info-row" v-if="getItemsWithLabels(detail, '11').length > 0">
                          <span class="info-label">کسورات:</span>
                            <div class="multi-values">
                              <span class="multi-value-item" v-for="item in getItemsWithLabels(detail, '11')" :key="item.key">
                                  {{ item.label }}: {{ formatNumber(item.value) }}
                              </span>
                            </div>
                          </div>
                          <!-- ezafat Section - Using bracket notation for numeric properties -->
                          <div class="info-row" v-if="getItemsWithLabels(detail, '12').length > 0">
                          <span class="info-label">اضافات:</span>
                            <div class="multi-values">
                                <span class="multi-value-item" v-for="item in getItemsWithLabels(detail, '12')" :key="item.key">
                                  {{ item.label }}: {{ formatNumber(item.value) }}
                                </span>
                            </div>
                          </div>
                          <div class="info-row" v-if="detail.value1">
                            <span class="info-label">مقدار خالص:</span>
                            <span class="info-value">{{ formatNumber(detail.value1) }}</span>
                          </div>
                      </div>
                      </div>

                      <!-- Amounts Section -->
                      <div class="detail-section">
                      <h4 class="section-title">مبالغ</h4>
                      <div class="info-box">
                          <div class="info-row">
                          <span class="info-label">فی:</span>
                          <span v-if="selectedCurrency==1" class="info-value amount">{{ formatCurrency(detail.Fe) }}</span>
                          <span v-else class="info-value amount">{{ formatCurrency(detail.Fe_A) }}</span>
                          </div>
                          <!-- <div class="info-row" v-if="detail.FeT">
                          <span class="info-label">فی کل:</span>
                          <span class="info-value amount">{{ formatCurrency(detail.FeT) }}</span>
                          </div> -->
                          <div class="info-row">
                          <span class="info-label">مبلغ کل:</span>
                          <span v-if="selectedCurrency==1" class="info-value amount total">{{ formatCurrency(detail.money) }}</span>
                          <span v-else class="info-value amount total">{{ formatCurrency(detail.money_A) }}</span>
                          </div>
                      </div>
                      </div>

                      <!-- Additional Information -->
                      <div class="detail-section" v-if="hasAdditionalInfo(detail)">
                      <h4 class="section-title">اطلاعات تکمیلی</h4>
                      <div class="info-box">
                          <div class="info-row" v-if="detail.Comment2 && detail.Comment2 !== '-'">
                          <span class="info-label">شرح 2:</span>
                          <span class="info-value">{{ detail.Comment2 }}</span>
                          </div>
                          <div class="info-row" v-if="detail.Comment && detail.Comment !== '-'">
                          <span class="info-label">شرح:</span>
                          <span class="info-value">{{ detail.Comment }}</span>
                          </div>
                          <div class="info-row" v-if="detail.Codeing">
                          <span class="info-label">کدینگ:</span>
                          <span class="info-value">{{ detail.Codeing }}</span>
                          </div>
                      </div>
                      </div>
                  </div>
                  </div>
              </div>

              <!-- Empty State -->
              <div v-else class="modal-empty">
                  <div class="empty-icon">📦</div>
                  <h4>هیچ اطلاعات جزئیاتی یافت نشد</h4>
                  <p>برای این فاکتور اطلاعات بازرگانی ثبت نشده است.</p>
              </div>
              </div>

              <div class="modal-footer">
              <button class="close-modal-btn" @click="closeCommercialModal">
                  بستن
              </button>
              </div>
          </div>
        </div>


        <div style="z-index: 999999;">

        <ChatWidget
        v-if="showChat"
        department="financial"
        :company-id="selectedCompanyId"
        :position="{ bottom: '20px', right: '20px' }"
        />

        </div>

    </div>
</template>

<script>
import { computed, onMounted, ref, watch } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { accountingService } from '@/services/api'
import Navbar from '@/components/Navbar.vue'
import { useRouter } from 'vue-router'
import DatePicker from 'vue3-persian-datetime-picker'
import ChatWidget from '../Messanger/ChatWidget.vue'

export default {
  name: 'FactorView',
  components: {
    Navbar,
    DatePicker,
    ChatWidget, 
  },
  setup() {
    const authStore = useAuthStore()
    const remainingCash = ref(0)
    const accountingData = ref([])
    const loading = ref(false)
    const error = ref(null)
    const searchQuery = ref('')
    const currentPage = ref(1)
    const itemsPerPage = 10
    const showCommercialModal = ref(false)
    const commercialDetails = ref([])
    const commercialLoading = ref(false)
    const commercialError = ref(null)
    const selectedInvoice = ref(null)
    const router = useRouter()

    const sortOrder = ref('desc')

    const showChat = computed(() => {
      // Show chat widget based on user role/needs
      return authStore.getCurrentRole === 'customer'
    })

    // Add filter states
    const showFilterPanel = ref(false)
    const filters = ref({
        transactionType: [], // ['debit', 'credit']
        dateFrom: '',
        dateTo: '',
        amountFrom: '',
        amountTo: ''
    })

    const arzList = ref([])
    const selectedCurrency = ref(1) // Default to Rial (ID: 1)
    const loadingArz = ref(false)

    // Fetch all ARZ (currencies)
    const fetchAllArz = async () => {
      loadingArz.value = true
      try {
        console.log('🔧 Fetching all ARZ...')
        const response = await accountingService.getAllArz()
        if (response.data.success) {
          arzList.value = response.data.data
          console.log('✅ ARZ list loaded:', arzList.value)
        } else {
          console.error('❌ Failed to load ARZ:', response.data.message)
        }
      } catch (err) {
        console.error('❌ Error fetching ARZ:', err)
        // You can show a toast notification here if needed
      } finally {
        loadingArz.value = false
      }
    }

    // Handle currency change
    const onCurrencyChange = () => {
      console.log('🔧 Currency changed to:', selectedCurrency.value)
      currentPage.value = 1
      fetchAccountingData()
    }

    // Get currency sign for display
    const getCurrencySign = () => {
      if (selectedCurrency.value === 1) return 'ریال' // Default Rial
      
      const selectedArz = arzList.value.find(arz => arz.idd === selectedCurrency.value)
      return selectedArz ? selectedArz.sign : 'ریال'
    }

    // Filter functions
    const toggleFilterPanel = () => {
      showFilterPanel.value = !showFilterPanel.value
    }

    const closeFilterPanel = () => {
      showFilterPanel.value = false
    }

    const applyFilters = () => {
      currentPage.value = 1
      closeFilterPanel()
    }

    const clearFilters = () => {
      filters.value = {
        transactionType: [],
        dateFrom: '',
        dateTo: '',
        amountFrom: '',
        amountTo: ''
      }
      currentPage.value = 1
    }

    const removeTransactionTypeFilter = () => {
      filters.value.transactionType = []
    }

    // Count active filters
    const activeFilterCount = computed(() => {
      let count = 0
      if (filters.value.transactionType.length > 0) count++
      if (filters.value.dateFrom) count++
      if (filters.value.dateTo) count++
      if (filters.value.amountFrom) count++
      if (filters.value.amountTo) count++
      return count
    })

    const getTransactionTypeText = () => {
      if (filters.value.transactionType.length === 2) return 'همه'
      if (filters.value.transactionType.includes('debit')) return 'بدهکار'
      if (filters.value.transactionType.includes('credit')) return 'بستانکار'
      return ''
    }

    const formatFilterDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('fa-IR')
    }

    // Enhanced filter function
    const filteredData = computed(() => {
      let filtered = accountingData.value

      // Apply search filter
      if (searchQuery.value.trim()) {
        const query = searchQuery.value.trim().toLowerCase()
        filtered = filtered.filter(record => {
          const description = (record.Comment2 || '').toLowerCase()
          const amount = (record.debitPrice || record.creditPrice || 0).toString()
          return description.includes(query) || amount.includes(query)
        })
      }

      // Apply transaction type filter
      if (filters.value.transactionType.length > 0) {
        filtered = filtered.filter(record => {
          if (filters.value.transactionType.includes('debit') && record.debitPrice) return true
          if (filters.value.transactionType.includes('credit') && record.creditPrice) return true
          return false
        })
      }

      // Apply date range filter
      if (filters.value.dateFrom) {
        const fromDate = new Date(filters.value.dateFrom)
        filtered = filtered.filter(record => {
          if (!record.Dates) return false
          const recordDate = new Date(record.Dates)
          return recordDate >= fromDate
        })
      }

      if (filters.value.dateTo) {
        const toDate = new Date(filters.value.dateTo)
        toDate.setHours(23, 59, 59, 999) // End of day
        filtered = filtered.filter(record => {
          if (!record.Dates) return false
          const recordDate = new Date(record.Dates)
          return recordDate <= toDate
        })
      }

      // Apply amount range filter
      if (filters.value.amountFrom) {
        const minAmount = parseFloat(filters.value.amountFrom)
        filtered = filtered.filter(record => {
          const amount = record.debitPrice || record.creditPrice || 0
          return amount >= minAmount
        })
      }

      if (filters.value.amountTo) {
        const maxAmount = parseFloat(filters.value.amountTo)
        filtered = filtered.filter(record => {
          const amount = record.debitPrice || record.creditPrice || 0
          return amount <= maxAmount
        })
      }

      return filtered
    })

    const navigateToDashboard = () => {
      router.push('/dashboard')
    }

    const isAuthenticated = computed(() => authStore.isAuthenticated)

    const toggleSortOrder = () => {
      //accountingData.value = []
      sortOrder.value = sortOrder.value === 'desc' ? 'asc' : 'desc'
      fetchAccountingData() // Refetch data with new sort order
    }

    // Filter data based on search query
    // const filteredData = computed(() => {
    //   if (!searchQuery.value.trim()) {
    //     return accountingData.value
    //   }
      
    //   const query = searchQuery.value.trim().toLowerCase()
    //   return accountingData.value.filter(record => {
    //     const description = (record.Comment2 || '').toLowerCase()
    //     const amount = (record.debitPrice || record.creditPrice || 0).toString()
        
    //     return description.includes(query) || amount.includes(query)
    //   })
    // })

    // Paginated data
    const paginatedData = computed(() => {
      const startIndex = (currentPage.value - 1) * itemsPerPage
      const endIndex = startIndex + itemsPerPage
      return filteredData.value.slice(startIndex, endIndex)
    })

    // Total pages
    const totalPages = computed(() => {
      return Math.ceil(filteredData.value.length / itemsPerPage)
    })

    // Pagination methods
    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++
      }
    }

    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--
      }
    }

    // Reset to first page when search changes
    watch(searchQuery, () => {
      currentPage.value = 1
    })

    // Print functionality
    const printData = () => {
      const printWindow = window.open('', '_blank')
      const tableContent = generatePrintableTable()
      
      printWindow.document.write(`
        <html dir="rtl">
          <head>
            <title>چاپ اطلاعات حسابداری</title>
            <style>
              body { 
                font-family: Tahoma, Arial, sans-serif; 
                margin: 20px;
                direction: rtl;
              }
              h1 { 
                text-align: center; 
                color: #333;
                margin-bottom: 20px;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 10px;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: right;
              }
              th {
                background-color: #f5f5f5;
                font-weight: bold;
              }
              .debit { color: #e74c3c; }
              .credit { color: #27ae60; }
              @media print {
                body { margin: 0; }
                table { width: 100%; }
              }
            </style>
          </head>
          <body>
            <h1>گزارش تراکنش‌های حسابداری</h1>
            <div>تاریخ چاپ: ${new Date().toLocaleDateString('fa-IR')}</div>
            <div>تعداد رکوردها: ${filteredData.value.length}</div>
            ${tableContent}
          </body>
        </html>
      `)
      
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 500)
    }

    const generatePrintableTable = () => {
      return `
        <table>
          <thead>
            <tr>
              <th>تاریخ</th>
              <th>شماره سند</th>
              <th>شرح</th>
              <th>مبلغ</th>
              <th>نوع</th>
              <th>ارز</th>
              <th>توضیحات</th>
            </tr>
          </thead>
          <tbody>
            ${filteredData.value.map(record => `
              <tr>
                <td>${record.Dates || '-'}</td>
                <td>${record.Exhibit_Auto || '-'}</td>
                <td>${record.Comment2 || 'بدون شرح'}</td>
                <td class="${record.debitPrice ? 'debit' : 'credit'}">
                  ${formatCurrency(record.debitPrice || record.creditPrice)}
                </td>
                <td>${record.debitPrice ? 'بدهکار' : 'بستانکار'}</td>
                <td>${record.ExhibitItem_arz2 || '-'}</td>
                <td>${record.St || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      `
    }

    // Fetch accounting data and remaining cash
    const fetchAccountingData = async () => {
      if (!authStore.user?.customerId) {
        console.error('No customer ID found')
        return
      }

      loading.value = true
      error.value = null

      try {
        console.log('Fetching data for customer ID:', authStore.user.subCodeId)
        console.log('Selected currency:', selectedCurrency.value)
        console.log('Sort order:', sortOrder.value)
        
        // Fetch remaining cash
        const cashResponse = await accountingService.getRemainingCash(selectedCurrency.value)
        if (cashResponse.data.success) {
          remainingCash.value = cashResponse.data.remaining_cash || 0
        }

        // Fetch accounting data with currency and sort parameters
        const accountingResponse = await accountingService.getAccountingData(
          selectedCurrency.value, // Pass selected currency
          'Dates', // Sort by Dates
          sortOrder.value // Use current sort order
        )
        if (accountingResponse.data.success) {
          accountingData.value = accountingResponse.data.data || []
        }

        console.log('Accounting data fetched:', accountingData.value.length, 'records')
        console.log('Remaining cash:', remainingCash.value)

      } catch (err) {
        console.error('Error fetching accounting data:', err)
        error.value = err.response?.data?.detail || 'خطا در دریافت اطلاعات'
      } finally {
        loading.value = false
      }
    }


    const formatCurrency = (amount) => {
      if (!amount && amount !== 0) return '۰'
      return new Intl.NumberFormat('fa-IR', {

      }).format(amount)
    }

    const formatRemainingCash = (amount) => {
      if (!amount && amount !== 0) return '۰'
      // Use absolute value for display
      const absoluteAmount = Math.abs(amount)
      return new Intl.NumberFormat('fa-IR').format(absoluteAmount).replace('ریال', '') + getCurrencySign()
    }

    const formatBestanKari = (amount) => {
      if (!amount && amount !== 0) return '۰'
      if (amount > 0){
        return 'بدهکار'
      }else {
        return 'بستانکار'
      }
    }

    const formatDate = (dateString) => {
      if (!dateString) return 'تاریخ نامعلوم'
      try {
        const date = new Date(dateString)
        return new Intl.DateTimeFormat('fa-IR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }).format(date)
      } catch {
        return dateString
      }
    }

    const formatPersianDate = (dateString) => {
      if (!dateString) return 'تاریخ نامعلوم'
      
      try {
        // Split the date string (format: 1404/06/18)
        const parts = dateString.split('/')
        if (parts.length !== 3) return dateString
        
        const year = parts[0]
        const month = parseInt(parts[1])
        const day = parts[2]
        
        // Persian month names
        const persianMonths = [
          'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
          'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
        ]
        
        if (month >= 1 && month <= 12) {
          return `${day} ${persianMonths[month - 1]} ${year}`
        }
        
        return dateString
      } catch {
        return dateString
      }
    }

    const openCommercialDetails = async (invoiceAuto) => {
      selectedInvoice.value = invoiceAuto.ID_Invoice
      showCommercialModal.value = true
      commercialLoading.value = true
      commercialError.value = null
      commercialDetails.value = []

      try {
        console.log('Fetching commercial details for invoice:', invoiceAuto)
        const response = await accountingService.getCommercialDetails(invoiceAuto.ID_Invoice, invoiceAuto.atf, invoiceAuto.Ac, invoiceAuto.Act)
        
        if (response.data.success) {
          commercialDetails.value = response.data.data
          console.log('Commercial details loaded:', commercialDetails.value)
        } else {
          commercialError.value = response.data.message || 'خطا در دریافت اطلاعات جزئیات'
        }
      } catch (err) {
        console.error('Error fetching commercial details:', err)
        
        // Handle specific error cases
        if (err.response?.data?.message) {
          commercialError.value = err.response.data.message
        } else if (err.code === 'NETWORK_ERROR') {
          commercialError.value = 'خطا در اتصال به سرور'
        } else {
          commercialError.value = 'خطای ناشناخته در دریافت اطلاعات'
        }
      } finally {
        commercialLoading.value = false
      }
    }

    const closeCommercialModal = () => {
      showCommercialModal.value = false
      commercialDetails.value = []
      commercialError.value = null
      selectedInvoice.value = null
    }

    const retryFetchCommercialDetails = () => {
      if (selectedInvoice.value) {
        openCommercialDetails(selectedInvoice.value)
      }
    }

    const hasAdditionalInfo = (detail) => {
      return (detail.Comment2 && detail.Comment2 !== '-') || 
             (detail.Comment && detail.Comment !== '-') || 
             detail.Codeing
    }

    const formatNumber = (number) => {
      if (!number && number !== 0) return '۰'
      return new Intl.NumberFormat('fa-IR').format(number)
    }

    onMounted(async () => {
      if (isAuthenticated.value) {
        console.log("User is authenticated, fetching accounting data...")
        await fetchAllArz()
        await fetchAccountingData()
      }
    })

    const printCommercialDetails = () => {
      // Check if we have data to print
      if (!commercialDetails.value || commercialDetails.value.length === 0) {
        alert('هیچ داده‌ای برای چاپ وجود ندارد')
        return
      }
      
      const printWindow = window.open('', '_blank', 'width=1200,height=800')
      
      if (!printWindow) {
        alert('لطفاً مسدود کننده پنجره‌های بازشو را غیرفعال کنید')
        return
      }
      
      const tableContent = generateCommercialDetailsTable()
      
      // Use a function to generate the print HTML to avoid escaping issues
      const getPrintContent = () => {
        return `
          <html dir="rtl">
            <head>
              <title>چاپ جزئیات بازرگانی - فاکتور ${selectedInvoice.value}</title>
              <style>
                * {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
                }
                body { 
                  font-family: 'Tahoma', 'Vazir', 'Segoe UI', Arial, sans-serif; 
                  margin: 20px;
                  padding: 20px;
                  direction: rtl;
                  line-height: 1.6;
                  background: #ffffff;
                  color: #333;
                }
                .print-header { 
                  text-align: center; 
                  color: #333;
                  margin-bottom: 30px;
                  padding-bottom: 15px;
                  border-bottom: 3px solid #667eea;
                }
                .print-header h1 {
                  margin: 0 0 10px 0;
                  color: #2d3436;
                  font-size: 24px;
                }
                .print-header .subtitle {
                  color: #666;
                  font-size: 16px;
                }
                .print-info {
                  display: flex;
                  justify-content: space-between;
                  flex-wrap: wrap;
                  margin-bottom: 20px;
                  padding: 15px 20px;
                  background: #f8f9fa;
                  border-radius: 8px;
                  border: 1px solid #e0e0e0;
                  gap: 10px;
                }
                .info-item {
                  display: flex;
                  flex-direction: column;
                }
                .info-label {
                  font-weight: bold;
                  color: #555;
                  margin-bottom: 5px;
                  font-size: 13px;
                }
                .info-value {
                  color: #333;
                  font-size: 14px;
                }
                .details-table {
                  width: 100%;
                  border-collapse: collapse;
                  margin-top: 20px;
                  font-size: 13px;
                }
                .details-table th {
                  background: #667eea;
                  color: white;
                  padding: 10px 6px;
                  text-align: center;
                  border: 1px solid #5a6fd6;
                  font-weight: bold;
                  font-size: 12px;
                }
                .details-table td {
                  padding: 8px 6px;
                  border: 1px solid #ddd;
                  text-align: center;
                  font-size: 12px;
                }
                .details-table tr:nth-child(even) {
                  background-color: #f8f9fa;
                }
                .details-table tr:hover {
                  background-color: #e9ecef;
                }
                .amount-cell {
                  font-weight: bold;
                  color: #2d3436;
                  direction: ltr;
                }
                .total-row {
                  background-color: #e3f2fd !important;
                  font-weight: bold;
                }
                .total-row td {
                  font-size: 13px;
                  padding: 10px 6px;
                }
                .text-center {
                  text-align: center;
                }
                .text-left {
                  text-align: left;
                }
                .text-right {
                  text-align: right;
                }
                .section-title {
                  color: #333;
                  border-bottom: 2px solid #667eea;
                  padding-bottom: 8px;
                  margin: 20px 0 15px 0;
                  font-size: 18px;
                }
                .footer {
                  margin-top: 30px;
                  text-align: center;
                  color: #888;
                  font-size: 12px;
                  border-top: 1px solid #eee;
                  padding-top: 20px;
                }
                @media print {
                  body { 
                    margin: 0; 
                    padding: 15px;
                    font-size: 11px;
                  }
                  .print-header {
                    margin-bottom: 15px;
                    padding-bottom: 10px;
                  }
                  .print-header h1 {
                    font-size: 20px;
                  }
                  .print-info {
                    padding: 10px 15px;
                    margin-bottom: 15px;
                  }
                  .details-table {
                    font-size: 10px;
                  }
                  .details-table th {
                    padding: 6px 4px;
                    font-size: 10px;
                  }
                  .details-table td {
                    padding: 5px 4px;
                    font-size: 10px;
                  }
                  .total-row td {
                    font-size: 11px;
                    padding: 8px 4px;
                  }
                  .no-print {
                    display: none !important;
                  }
                  .section-title {
                    font-size: 16px;
                    margin: 15px 0 10px 0;
                  }
                  .footer {
                    margin-top: 20px;
                    padding-top: 15px;
                  }
                }
                @page {
                  margin: 0.5cm;
                  size: A4 portrait;
                }
              </style>
            </head>
            <body>
              <div class="print-header">
                <h1>📄 گزارش جزئیات بازرگانی</h1>
                <div class="subtitle">فاکتور شماره: ${selectedInvoice.value}</div>
              </div>
              
              <div class="print-info">
                <div class="info-item">
                  <span class="info-label">📅 تاریخ چاپ:</span>
                  <span class="info-value">${new Date().toLocaleDateString('fa-IR')}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">📊 تعداد آیتم‌ها:</span>
                  <span class="info-value">${commercialDetails.value.length} مورد</span>
                </div>
                <div class="info-item">
                  <span class="info-label">💰 مجموع مبالغ:</span>
                  <span class="info-value">${formatCurrency(commercialDetails.value.reduce((sum, item) => {
                    const money = selectedCurrency.value == 1 ? item.money : item.money_A
                    return sum + (parseFloat(money) || 0)
                  }, 0))}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">💱 ارز:</span>
                  <span class="info-value">${getCurrencySign()}</span>
                </div>
              </div>
              
              ${tableContent}
              
              <div class="footer">
                <p>الماس هوشمند - سیستم حسابداری پیشرفته</p>
                <p>تاریخ تولید گزارش: ${new Date().toLocaleString('fa-IR')}</p>
              </div>
            </body>
          </html>
        `
      }
      
      // Write the content without the script tag in the HTML
      printWindow.document.write(getPrintContent())
      printWindow.document.close()
      printWindow.focus()
      
      // Trigger print from the parent window after content loads
      setTimeout(() => {
        printWindow.print()
      }, 1000)
    }

    const generateCommercialDetailsTable = () => {
      if (!commercialDetails.value.length) return '<p>هیچ داده‌ای برای نمایش وجود ندارد</p>'
      
      // Helper function to get amari items
      const getAmariItems = (detail) => {
        return getItemsWithLabels(detail, '13').map(item => 
          `${item.label}: ${formatNumber(item.value)}`
        ).join(' | ')
      }
      
      // Helper function to get ksorat items
      const getKsoratItems = (detail) => {
        return getItemsWithLabels(detail, '11').map(item => 
          `${item.label}: ${formatNumber(item.value)}`
        ).join(' | ')
      }
      
      // Helper function to get ezafat items
      const getEzafatItems = (detail) => {
        return getItemsWithLabels(detail, '12').map(item => 
          `${item.label}: ${formatNumber(item.value)}`
        ).join(' | ')
      }
      
      // Function to format weight (container)
      const formatWeightZarf = (detail) => {
        const ksoratItems = getItemsWithLabels(detail, '11')
        const ezafatItems = getItemsWithLabels(detail, '12')
        
        // Find weight-related items
        const ksoratWeight = ksoratItems.find(item => 
          item.label.includes('وزن') || item.label.includes('ظرف')
        )
        const ezafatWeight = ezafatItems.find(item => 
          item.label.includes('وزن') || item.label.includes('ظرف')
        )
        
        if (ksoratWeight) return ksoratWeight.value
        if (ezafatWeight) return ezafatWeight.value
        return '-'
      }
      
      // Calculate totals
      const totalFe = commercialDetails.value.reduce((sum, item) => {
        const fe = selectedCurrency.value == 1 ? item.Fe : item.Fe_A
        return sum + (parseFloat(fe) || 0)
      }, 0)
      
      const totalMoney = commercialDetails.value.reduce((sum, item) => {
        const money = selectedCurrency.value == 1 ? item.money : item.money_A
        return sum + (parseFloat(money) || 0)
      }, 0)
      
      return `
        <table class="details-table">
          <thead>
            <tr>
              <th width="12%">عنوان کالا</th>
              <th width="7%">واحد</th>
              <th width="10%">آماری</th>
              <th width="8%">وزن پر</th>
              <th width="8%">وزن خالی</th>
              <th width="8%">مقدار ناخالص</th>
              <th width="10%">کسورات</th>
              <th width="8%">اضافات</th>
              <th width="8%">وزن ظرف</th>
              <th width="8%">مقدار خالص</th>
              <th width="10%">فی (${getCurrencySign()})</th>
              <th width="3%">مبلغ کل (${getCurrencySign()})</th>
            </tr>
          </thead>
          <tbody>
            ${commercialDetails.value.map((detail) => {
              const feValue = selectedCurrency.value == 1 ? detail.Fe : detail.Fe_A
              const moneyValue = selectedCurrency.value == 1 ? detail.money : detail.money_A
              
              return `
                <tr>
                  <td><strong>${detail.KalaName || detail.Name || 'نامشخص'}</strong></td>
                  <td class="text-center">${detail.vahed || '-'}</td>
                  <td class="text-center" style="font-size: 11px;">
                    ${getItemsWithLabels(detail, '13').length > 0 ? getAmariItems(detail) : '-'}
                  </td>
                  <td class="text-center">${detail.GM7 ? formatNumber(detail.GM7) : '-'}</td>
                  <td class="text-center">${detail.GM8 ? formatNumber(detail.GM8) : '-'}</td>
                  <td class="text-center">${detail.GM6 ? formatNumber(detail.GM6) : '-'}</td>
                  <td class="text-center" style="font-size: 11px;">
                    ${getItemsWithLabels(detail, '11').length > 0 ? getKsoratItems(detail) : '-'}
                  </td>
                  <td class="text-center" style="font-size: 11px;">
                    ${getItemsWithLabels(detail, '12').length > 0 ? getEzafatItems(detail) : '-'}
                  </td>
                  <td class="text-center">${formatWeightZarf(detail)}</td>
                  <td class="text-center">${detail.value1 ? formatNumber(detail.value1) : '-'}</td>
                  <td class="amount-cell">${feValue ? formatCurrency(feValue) : '-'}</td>
                  <td class="amount-cell" style="color: #e74c3c;">
                    <strong>${moneyValue ? formatCurrency(moneyValue) : '-'}</strong>
                  </td>
                </tr>
              `
            }).join('')}
            
            <!-- Total Row -->
            <tr class="total-row">
              <td colspan="10" style="text-align: left;">
                <strong>جمع کل:</strong>
              </td>
              <td class="amount-cell">
                ${formatCurrency(totalFe)}
              </td>
              <td class="amount-cell" style="color: #e74c3c;">
                <strong>
                  ${formatCurrency(totalMoney)}
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
        
        <!-- Additional Information Section -->
        ${generateAdditionalInfoTable()}
      `
    }

    const generateAdditionalInfoTable = () => {
      const itemsWithAdditionalInfo = commercialDetails.value.filter(item => 
        (item.Comment2 && item.Comment2 !== '-') || 
        (item.Comment && item.Comment !== '-') || 
        item.Codeing
      )
      
      if (itemsWithAdditionalInfo.length === 0) return ''
      
      return `
        <div style="margin-top: 30px;">
          <h3 style="color: #333; border-bottom: 2px solid #667eea; padding-bottom: 8px;">اطلاعات تکمیلی</h3>
          <table class="details-table">
            <thead>
              <tr>
                <th width="25%">عنوان کالا</th>
                <th width="15%">کدینگ</th>
                <th width="30%">شرح</th>
                <th width="30%">شرح ۲</th>
              </tr>
            </thead>
            <tbody>
              ${itemsWithAdditionalInfo.map(item => `
                <tr>
                  <td><strong>${item.KalaName || item.Name || 'نامشخص'}</strong></td>
                  <td class="text-center">${item.Codeing || '-'}</td>
                  <td>${item.Comment && item.Comment !== '-' ? item.Comment : '-'}</td>
                  <td>${item.Comment2 && item.Comment2 !== '-' ? item.Comment2 : '-'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `
    }

    const getNumbericalItems = (prefix) => {
        if (!commercialDetails.value.length) return []
        
        const currentDetail = commercialDetails.value[0]
        const items = []
        
        for (const [key, value] of Object.entries(currentDetail)) {
            if (key.startsWith(prefix) && value && value !== 0) {
                items.push({ key, value })
            }
        }
        
        return items
    }

    const getItemsWithLabels = (detail, prefix) => {
      const items = []
      const fieldLabels = detail._field_labels || {}
      
      for (const [key, value] of Object.entries(detail)) {
          if (key.startsWith(prefix) && value && value !== 0) {
              const label = fieldLabels[key] || key // Use label if available, otherwise use key
              items.push({ key, value, label })
          }
      }
      
      return items
    }

    const selectedCompanyId = computed(() => {
      // Get the selected company from auth store or localStorage
      const selectedCompany = JSON.parse(localStorage.getItem('selectedCompany') || '{}')
      return selectedCompany.company_id || authStore.getSelectedCompany?.company_id || 1 // Default to 1
    })

    return {
      arzList,
      selectedCurrency,
      loadingArz,
      getCurrencySign,
      onCurrencyChange,
      sortOrder,
      showFilterPanel,
      remainingCash,
      accountingData,
      filteredData,
      filters,
      activeFilterCount,
      paginatedData,
      loading,
      error,
      searchQuery,
      currentPage,
      totalPages,
      isAuthenticated,
      showCommercialModal,
      commercialDetails,
      commercialLoading,
      commercialError,
      selectedInvoice,
      showChat,
      getNumbericalItems,
      getItemsWithLabels,
      formatCurrency,
      formatBestanKari,
      formatPersianDate,
      formatDate,
      nextPage,
      prevPage,
      printData,
      openCommercialDetails,
      closeCommercialModal,
      retryFetchCommercialDetails,
      hasAdditionalInfo,
      formatNumber,
      printCommercialDetails,
      toggleFilterPanel,
      closeFilterPanel,
      applyFilters,
      clearFilters,
      removeTransactionTypeFilter,
      getTransactionTypeText,
      formatFilterDate,
      navigateToDashboard,
      toggleSortOrder,
      formatRemainingCash,
      selectedCompanyId,
      truncateText: (text, maxLength) => {
        if (!text) return ''
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
      }
    }
  }
}
</script>

<style scoped>
.bank-app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f5f5;
  direction: rtl;
}
.account-summary {
  background: linear-gradient(135deg, #4a6baf, #2c4a8c);
  color: white;
  border-radius: 16px;
  display: inline-flex;
  justify-content: center;
  padding: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.account-type {
  font-size: 16px;
  margin-bottom: 8px;
  margin-left: 5px;
  opacity: 0.9;
}

.account-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-label {
  font-size: 14px;
  opacity: 0.8;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
  margin-left: auto;
  margin-right: auto;
}
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #667eea;
}

.section-title {
  font-size: 1.1rem;
  color: #333;
  margin: 0;
}

.back-arrow {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  transition: background-color 0.3s ease;
}

.back-arrow:hover {
  background-color: #f0f0f0;
}
.action-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 0 10px;
}

.print-btn {
  background: #2c3e50;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
}

.print-btn:hover {
  background: #34495e;
}

.search-container {
  position: relative;
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  direction: rtl;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

.accounting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9000;
  padding: 20px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: white;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.modal-body {
  flex: 1;
  overflow-y: auto;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
}

/* Loading State */
.modal-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.modal-loading .loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Error State */
.modal-error {
  text-align: center;
  padding: 40px;
  color: #d63031;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.modal-error h4 {
  margin: 0 0 10px 0;
  color: #d63031;
}

.modal-error p {
  margin: 0 0 20px 0;
  color: #666;
}

.retry-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: transform 0.2s;
}

.retry-btn:hover {
  transform: translateY(-2px);
}

/* Empty State */
.modal-empty {
  text-align: center;
  padding: 40px;
  color: #666;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 15px;
}

.modal-empty h4 {
  margin: 0 0 10px 0;
  color: #333;
}

/* Commercial Details Styles */
.commercial-details {
  margin-top: 10px;
}

.details-grid {
  display: grid;
  gap: 20px;
}

.detail-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.detail-section {
  margin-bottom: 20px;
}

.detail-section:last-child {
  margin-bottom: 0;
}

.info-box {
  background: white;
  border-radius: 6px;
  padding: 15px;
  border: 1px solid #e0e0e0;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
}

.info-value {
  color: #333;
  font-size: 0.9rem;
}

        /* Multi-values styling */
        .multi-values {
            display: flex;
            flex-direction: column;
            gap: 5px;
            flex: 1;
        }
        
        .multi-value-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 5px 0;
        }
        
        .multi-value-code {
            font-weight: 600;
            color: #4b6cb7;
            margin-left: 10px;
        }
        
        .multi-value-amount {
            color: #2c3e50;
            font-weight: 500;
        }

.info-value.amount {
  font-weight: 600;
  color: #2d3436;
}

.info-value.amount.total {
  color: #e74c3c;
  font-size: 1rem;
}

/* Details Button */
.details-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  margin-top: 10px;
  transition: all 0.3s ease;
  width: 100%;
}

.details-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}

.details-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.close-modal-btn {
  background: #6c757d;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.close-modal-btn:hover {
  background: #5a6268;
}

/* Responsive Design */
@media (max-width: 768px) {
  
  .modal-header {
    padding: 15px;
  }
  
  .detail-card {
    padding: 15px;
  }
  
  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 5px;
  }
  
  .info-label, .info-value {
    font-size: 0.85rem;
  }
}
.modal-actions {
  display: flex;
  gap: 10px;
  align-items: center;
}

.print-modal-btn {
  background: linear-gradient(135deg, #27ae60 0%, #2ecc71 100%);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 5px;
}

.print-modal-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(39, 174, 96, 0.3);
}

/* Update modal header to accommodate the print button */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

/* Ensure the header content is properly aligned */
.modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
  flex: 1;
}

/* Responsive adjustments for mobile */
@media (max-width: 768px) {
  .modal-actions {
    gap: 8px;
  }
  
  .print-modal-btn {
    padding: 6px 12px;
    font-size: 0.8rem;
  }
  
  .modal-header {
    padding: 15px;
  }
}
.accounting-section {
  padding-bottom: 45px;
  padding-top: 15px;
  background-color: var(--bg);
}

.accounting-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.accounting-card {
  background: white;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid #f0f0f0;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.accounting-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  /*padding-bottom: 12px;
   border-bottom: 1px solid #f0f0f0; */
}

.record-date {
  font-size: 12px;
  color: #666;
  font-weight: 500;
}

.record-amount {
  font-size: 14px;
  font-weight: bold;
}

.record-amount.debit {
  color: #e74c3c; /* Red for debit */
}

.record-amount.credit {
  color: #27ae60; /* Green for credit */
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.detail-label {
  font-size: 11px;
  color: #888;
  font-weight: 500;
  min-width: 60px;
}

.detail-value {
  font-size: 12px;
  color: #333;
  text-align: left;
  flex: 1;
}

.type-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: bold;
  text-align: center;
}

.debit-badge {
  background-color: #ffeaea;
  color: #e74c3c;
}

.credit-badge {
  background-color: #e8f7ef;
  color: #27ae60;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #4a6baf;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #888;
  background: white;
  border-radius: 12px;
  border: 1px dashed #ddd;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .accounting-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  
  .accounting-card {
    padding: 12px;
  }
  
  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  
  .record-amount {
    align-self: flex-end;
  }
}

@media (min-width: 769px) and (max-width: 1024px) {
  .accounting-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1025px) {
  .accounting-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}



.debit-badge {
  background-color: #e74c3c; /* Red for بد */
}

.credit-badge {
  background-color: #27ae60; /* Green for بس */
}

.record-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.debit {
  color: #e74c3c;
}

.credit {
  color: #27ae60;
}

.record-detail {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
}

.comment-section {
  flex: 1;
  min-width: 0; /* Important for text truncation */
  text-align: right; /* For RTL languages */
}

.amount-section {
  display: flex;
  align-items: center;
  gap: 1px;
  flex-shrink: 0;
}

.detail-value {
  font-size: 12px;
  color: var(--text);
  text-align: right; /* For RTL */
  word-wrap: break-word;
  display: block;
}

.record-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.transaction-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  border-radius: 50%;
  font-size: 0.8rem;
  font-weight: bold;
  color: white;
}
.sort-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  font-size: 14px;
}

.sort-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.sort-text {
  font-size: 12px;
  color: #495057;
}

.action-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
}
.action-bar {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

/* Filter Button */
.filter-btn {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
  font-size: 14px;
  position: relative;
}

.filter-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.filter-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Filter Panel */
.filter-panel {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 0;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
  border-radius: 8px 8px 0 0;
}

.filter-header h4 {
  margin: 0;
  color: #495057;
}

.close-filter-btn {
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #6c757d;
}

.filter-content {
  padding: 20px;
}

.filter-group {
  margin-bottom: 20px;
}

.filter-label {
  display: block;
  margin-bottom: 10px;
  font-weight: 600;
  color: #495057;
}

.filter-options {
  display: flex;
  gap: 15px;
}

.filter-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.filter-option input[type="checkbox"] {
  margin: 0;
}

.date-inputs, .amount-inputs {
  display: flex;
  gap: 15px;
}

.date-input, .amount-input {
  flex: 1;
}

.date-input label, .amount-input label {
  display: block;
  margin-bottom: 5px;
  font-size: 14px;
  color: #6c757d;
}

.date-picker, .amount-picker {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
}

.filter-actions {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.apply-filters-btn, .clear-filters-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.apply-filters-btn {
  background: #007bff;
  color: white;
}

.clear-filters-btn {
  background: #6c757d;
  color: white;
}

/* Active Filters */
.active-filters {
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
}

.active-filters-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.clear-all-filters {
  background: none;
  border: none;
  color: #e74c3c;
  cursor: pointer;
  font-size: 12px;
}

.active-filter-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-tag {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 16px;
  padding: 6px 12px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter-tag button {
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  font-size: 14px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .search-container{
    width: 100%;
  }
  .action-bar {
    flex-direction: row;
    align-items: center;
  }
  
  .date-inputs, .amount-inputs {
    flex-direction: column;
    gap: 10px;
  }
  
  .filter-options {
    flex-direction: column;
    gap: 10px;
  }
  
  .active-filter-tags {
    flex-direction: column;
  }
}
.currency-selector {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-right: 15px;
}

.currency-label {
  font-size: 0.9rem;
  color: #555;
  white-space: nowrap;
}

.currency-select {
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  font-size: 0.9rem;
  min-width: 120px;
}

.currency-select:disabled {
  background-color: #f5f5f5;
  opacity: 0.7;
}

.currency-loading {
  font-size: 0.8rem;
  color: #666;
  white-space: nowrap;
}

/* Responsive design for currency selector */
@media (max-width: 768px) {
  .currency-selector {
    margin-right: 10px;
  }
  
  .currency-select {
    min-width: 100px;
    font-size: 0.8rem;
  }
  
  .currency-label {
    font-size: 0.8rem;
  }
}

/* Update the action-bar to accommodate the new currency selector */
.action-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
  margin-bottom: 15px;
}

/* Ensure proper spacing in mobile view */
@media (max-width: 768px) {
  .action-bar {
    gap: 8px;
    padding: 10px;
  }
}

.info-row {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  width: 100%;
}

.info-label {
  font-weight: 600;
  color: #555;
  font-size: 0.9rem;
  width: 120px; /* Fixed width for perfect alignment */
  text-align: right;
  flex-shrink: 0;
  margin-left: 10px;
}

.info-value {
  color: #333;
  font-size: 0.9rem;
  text-align: right;
  flex: 1;
  padding-right: 15px;
}

/* Mobile styles */
@media (max-width: 768px) {
    .info-row {
        flex-direction: row;
        align-items: center;
        padding: 10px 0;
    }
    
    .info-label {
        width: 100px;
        text-align: right;
        margin-left: 5px;
        margin-bottom: 0;
        font-size: 0.85rem;
    }
    
    .info-value {
        width: auto;
        text-align: right;
        padding-right: 5px;
        font-size: 0.85rem;
        font-weight: 500;
        flex: 1;
    }
}

.bank-app {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--bg);
  color: var(--text);
}

/* Account Summary */

.account-summary {
  background: var(--primary-gradient);
  color: var(--text);
  border-radius: 16px;
  display: inline-flex;
  justify-content: center;
  padding: 10px;
  box-shadow: var(--shadow);
}

.account-type {
  font-size: 16px;
  margin-bottom: 8px;
  margin-left: 5px;
  opacity: .9;
}

.account-balance {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.balance-amount {
  font-size: 18px;
  font-weight: bold;
  margin-left: auto;
  margin-right: auto;
}

/* Section */

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid var(--primary);
}

.section-title {
  font-size: 1.1rem;
  color: var(--text);
  margin: 0;
}

.back-arrow {
  background: transparent;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  color: var(--text);
}

/* Action Bar */

.action-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 15px;
  background: var(--card-bg);
  border-radius: 10px;
  margin-bottom: 15px;
  border: 1px solid var(--border);
}

/* Buttons */

.print-btn {
  background: var(--primary);
  color: var(--on-primary);
  border: none;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
}

.print-btn:hover {
  opacity: .9;
}

.sort-btn,
.filter-btn {
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text);
}

.sort-btn:hover,
.filter-btn:hover {
  background: var(--hover-bg);
}

.sort-text {
  font-size: 12px;
  color: var(--text-muted);
}

/* Search */

.search-container {
  position: relative;
  width: 300px;
}

.search-input {
  width: 100%;
  padding: 10px 40px 10px 15px;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 14px;
  background: var(--input-bg);
  color: var(--text);
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
}

/* Currency */

.currency-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.currency-label {
  font-size: .9rem;
  color: var(--text-muted);
}

.currency-select {
  padding: 6px 10px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--input-bg);
  color: var(--text);
}

/* Cards */

.accounting-card {
  background: var(--card-bg);
  border-radius: 12px;
  padding: 16px;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
  transition: .2s;
}

.accounting-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow);
}

.record-date {
  font-size: 12px;
  color: var(--text-muted);
}

.record-amount {
  font-weight: bold;
  font-size: 1.1rem;
}

.debit {
  color: var(--danger);
}

.credit {
  color: var(--success);
}

/* Badges */

.transaction-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  border-radius: 50%;
  font-size: .8rem;
  font-weight: bold;
  color: #fff;
}

.debit-badge {
  background: var(--danger);
}

.credit-badge {
  background: var(--success);
}

/* Modal */

.modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--overlay-bg);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9000;
  padding: 20px;
}

.modal-content {
  background: var(--card-bg);
  border-radius: 12px;

  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  background: var(--primary-gradient);
  color: var(--on-primary);
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 24px;
  color: var(--on-primary);
  cursor: pointer;
}

.modal-body {
  
  overflow-y: auto;
}

.modal-footer {
  padding: 15px 20px;
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: flex-end;
}

.close-modal-btn {
  background: var(--secondary);
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
}

/* Loading */

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  color: var(--text-muted);
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border);
  border-top: 3px solid var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Empty */

.empty-state {
  text-align: center;
  padding: 40px;
  color: var(--text-muted);
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px dashed var(--border);
}

/* Filters */

.filter-panel {
  background: var(--card-bg);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: var(--shadow);
}

.filter-header {
  display: flex;
  justify-content: space-between;
  padding: 15px 20px;
  border-bottom: 1px solid var(--border);
}

.filter-label {
  font-weight: 600;
  color: var(--text);
}

.date-picker,
.amount-picker {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--input-bg);
  color: var(--text);
}

.apply-filters-btn {
  background: var(--primary);
  color: var(--on-primary);
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
}

.clear-filters-btn {
  background: var(--secondary);
  color: #fff;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
}

/* Animations */

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Responsive */

@media (max-width:768px) {

  .search-container{
    width:100%;
  }

  .accounting-grid{
    grid-template-columns:1fr;
  }

}

</style>